/**
 * Bloc « Code promo / Carte cadeau » du panier v2 (AdvantagesAccordion.tsx) : fonctions pures, testées
 * (advantages.test.ts). Elles ne font que LIRE les valeurs que le Nouveau Panier écrit dans son formulaire
 * (usePromoCodeApply : PromoCode, DiscountedAmount ; useGiftCardApply : GiftCard, GiftCardBalance) ; aucun calcul de
 * prix ici qui ne soit déjà celui du récapitulatif (OrderSummary : carte cadeau imputée = min(solde, total)).
 */
import type { Product } from 'screens/Checkout/useCheckoutForm';
import { formatAmount } from 'screens/Checkout/v2/format';

export interface AdvantagesValues {
  /** Code promo appliqué (vide : aucun). */
  promoCode?: string;
  /** Remise du code promo, en euros. */
  discount?: number;
  /** Numéro de la carte cadeau appliquée (vide : aucune). */
  giftCard?: string;
  /** Solde de la carte cadeau, en euros. */
  giftCardBalance?: number;
  /** Total après remise, avant carte cadeau (TotalAmount du formulaire). */
  totalAmount?: number;
}

const positive = (n: number | undefined): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0);

/**
 * Résumé affiché dans l'en-tête de l'accordéon quand un avantage est appliqué, null sinon :
 * « −10 € · CODE10 », « −50 € · Carte cadeau », « −60 € · CODE10 + carte cadeau ».
 * Montant = remise du code + part de la commande réglée par la carte (comme les lignes du récapitulatif).
 */
export function advantagesSummary(v: AdvantagesValues): string | null {
  const code = (v.promoCode ?? '').trim();
  const hasGift = !!(v.giftCard ?? '').trim() && positive(v.giftCardBalance) > 0;
  if (!code && !hasGift) return null;

  const discount = code ? positive(v.discount) : 0;
  const gift = hasGift ? Math.min(positive(v.giftCardBalance), positive(v.totalAmount)) : 0;
  const total = Math.round((discount + gift) * 100) / 100;

  const what = code && hasGift ? `${code} + carte cadeau` : code || 'Carte cadeau';
  return total > 0 ? `−${formatAmount(total)} · ${what}` : what;
}

/**
 * Ouverture d'office : un avantage déjà appliqué (son détail et « Retirer » restent sous les yeux) ou une erreur à
 * afficher (code refusé). Sinon fermé : le bloc est facultatif, le moyen de paiement passe devant.
 */
export function shouldOpenAdvantages({ applied, error }: { applied: boolean; error: boolean }): boolean {
  return applied || error;
}

/** Réglage BO « Cartes cadeaux » (window.mondayMindbody.giftCardConfig ; absent = carte proposée partout). */
export type GiftCardConfig = { restrictToEligible: boolean; eligibleServiceIds: string[] } | undefined;

/**
 * Le bloc Avantages du Nouveau Panier propose-t-il la carte cadeau pour ce produit ? Même règle, recopiée, que
 * `giftCardAllowed` de screens/Checkout/v2/Advantages.tsx (fichier v1 non modifié, SPEC § 6.2 règle 1) : packs
 * seulement ; réglage restreint → services de la liste seulement. Sert au titre de l'accordéon, qui ne doit pas
 * promettre une carte cadeau absente du panneau ; test de cohérence avec le bloc rendu (AdvantagesAccordion.test.tsx).
 */
export function giftCardOffered(product: Pick<Product, 'type' | 'Id'>, config: GiftCardConfig): boolean {
  return product.type === 'service' && (!config?.restrictToEligible || config.eligibleServiceIds.includes(String(product.Id)));
}
